import { useRef, useState } from 'react'
import { AlertCircle, CheckCircle, ChefHat, Download, FileText, Loader2, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useRecipeStore } from '@/stores/recipe-store'
import { useAuthStore } from '@/stores/auth-store'
import { recipeApi } from '@/services/recipe-api'
import { dutchClassics } from '@/data/dutch-classics'

export function ImportExportSettings() {
  const { recipes, loadRecipes } = useRecipeStore()
  const { user } = useAuthStore()
  const [importing, setImporting] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [seedingDutchRecipes, setSeedingDutchRecipes] = useState(false)
  const [importResult, setImportResult] = useState<{
    success: boolean
    message: string
    imported?: number
    errors?: number
  } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleExport = async () => {
    if (!user) return

    setExporting(true)
    try {
      await loadRecipes()

      const exportData = {
        version: '1.0',
        timestamp: new Date().toISOString(),
        user: {
          name: user.name,
          email: user.email,
        },
        recipes: recipes.map((recipe) => ({
          ...recipe,
          id: undefined,
          user_id: undefined,
          created_at: undefined,
          updated_at: undefined,
        })),
      }

      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json',
      })

      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `kookinspiratie-recepten-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Export error:', error)
      setImportResult({
        success: false,
        message: 'Export mislukt. Probeer het later opnieuw.',
      })
    } finally {
      setExporting(false)
    }
  }

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setImporting(true)
    setImportResult(null)

    try {
      const text = await file.text()
      const data = JSON.parse(text)

      if (!data.recipes || !Array.isArray(data.recipes)) {
        throw new Error('Ongeldig bestandsformaat')
      }

      let imported = 0
      let errors = 0

      for (const recipe of data.recipes) {
        try {
          await recipeApi.createRecipe({
            title: recipe.title || 'Untitled Recipe',
            description: recipe.description || '',
            ingredients: recipe.ingredients || [],
            instructions: recipe.instructions || [],
            prep_time: recipe.prep_time || null,
            cook_time: recipe.cook_time || null,
            servings: recipe.servings || null,
            difficulty: recipe.difficulty || '',
            meal_type: recipe.meal_type || '',
            tags: recipe.tags || [],
            equipment: recipe.equipment || [],
            notes: recipe.notes || '',
            rating: recipe.rating || null,
            image_url: recipe.image_url || '',
          })
          imported++
        } catch (err) {
          console.error('Failed to import recipe:', recipe.title, err)
          errors++
        }
      }

      await loadRecipes()

      setImportResult({
        success: true,
        message: `Import voltooid: ${imported} recepten geïmporteerd${errors > 0 ? `, ${errors} fouten` : ''}`,
        imported,
        errors,
      })
    } catch (error) {
      console.error('Import error:', error)
      setImportResult({
        success: false,
        message: error instanceof Error ? error.message : 'Import mislukt. Controleer het bestandsformaat.',
      })
    } finally {
      setImporting(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleSeedDutchClassics = async () => {
    if (!user) return

    setSeedingDutchRecipes(true)
    setImportResult(null)

    try {
      await loadRecipes()
      const currentRecipes = useRecipeStore.getState().recipes
      const recipesByTitle = new Map(
        currentRecipes.map((recipe) => [recipe.title.trim().toLowerCase(), recipe] as const)
      )

      let imported = 0
      let skipped = 0
      let promoted = 0
      let errors = 0

      for (const recipe of dutchClassics) {
        const titleKey = recipe.title.trim().toLowerCase()
        const existingRecipe = recipesByTitle.get(titleKey)

        if (existingRecipe) {
          if (existingRecipe.user_id === user.id && !existingRecipe.household_id && !existingRecipe.is_public) {
            try {
              await recipeApi.updateRecipe(existingRecipe.id, { is_public: true })
              promoted++
            } catch (error) {
              console.error('Failed to promote Dutch classic to public:', recipe.title, error)
              errors++
            }
            continue
          }

          skipped++
          continue
        }

        try {
          const createdRecipe = await recipeApi.createRecipe(recipe)
          recipesByTitle.set(titleKey, createdRecipe)
          imported++
        } catch (error) {
          console.error('Failed to seed Dutch classic:', recipe.title, error)
          errors++
        }
      }

      await loadRecipes()

      setImportResult({
        success: errors === 0,
        message: `Nederlandse klassiekers voltooid: ${imported} toegevoegd, ${promoted} publiek gemaakt, ${skipped} overgeslagen${errors > 0 ? `, ${errors} fouten` : ''}.`,
        imported,
        errors,
      })
    } catch (error) {
      console.error('Dutch classics seed error:', error)
      setImportResult({
        success: false,
        message: error instanceof Error ? error.message : 'Toevoegen van Nederlandse klassiekers mislukt.',
      })
    } finally {
      setSeedingDutchRecipes(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <FileText className="mr-2 h-5 w-5" />
          Import & Export
        </CardTitle>
        <p className="text-sm text-gray-600">Backup en herstel je receptenverzameling.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="font-medium text-green-800">Recepten Exporteren</h4>
              <p className="mt-1 text-sm text-green-600">
                Download al je recepten als JSON-bestand voor backup of om over te zetten naar een ander account.
              </p>
              <p className="mt-2 text-xs text-green-500">{recipes.length} recepten beschikbaar voor export</p>
            </div>
            <Button
              onClick={handleExport}
              disabled={exporting || recipes.length === 0}
              variant="outline"
              className="border-green-300 text-green-700 hover:bg-green-100"
            >
              {exporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Exporteren...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Exporteren
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="font-medium text-blue-800">Recepten Importeren</h4>
              <p className="mt-1 text-sm text-blue-600">
                Upload een JSON-bestand met recepten om ze toe te voegen aan je verzameling.
              </p>
              <div className="mt-2 space-y-1">
                <p className="text-xs text-blue-500">• Ondersteunt KookInspiratie JSON-exportbestanden</p>
                <p className="text-xs text-blue-500">• Recepten worden als privé geïmporteerd</p>
                <p className="text-xs text-blue-500">• Dubbele recepten worden als nieuwe recepten toegevoegd</p>
              </div>
            </div>
            <Button
              onClick={handleImportClick}
              disabled={importing}
              variant="outline"
              className="border-blue-300 text-blue-700 hover:bg-blue-100"
            >
              {importing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Importeren...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Importeren
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="font-medium text-orange-800">Nederlandse Klassiekers Toevoegen</h4>
              <p className="mt-1 text-sm text-orange-700">
                Voeg een samengestelde set bekende Nederlandse gerechten toe als publieke basisrecepten onder jouw account.
              </p>
              <div className="mt-2 space-y-1">
                <p className="text-xs text-orange-600">• Wordt opgeslagen onder jouw account en is zichtbaar in Zoeken</p>
                <p className="text-xs text-orange-600">• Bestaande recepten met dezelfde titel worden overgeslagen</p>
                <p className="text-xs text-orange-600">
                  • Bevat {dutchClassics.length} klassiekers zoals boerenkool, erwtensoep en appeltaart
                </p>
              </div>
            </div>
            <Button
              onClick={handleSeedDutchClassics}
              disabled={seedingDutchRecipes}
              variant="outline"
              className="border-orange-300 text-orange-700 hover:bg-orange-100"
            >
              {seedingDutchRecipes ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Toevoegen...
                </>
              ) : (
                <>
                  <ChefHat className="mr-2 h-4 w-4" />
                  Voeg Toe
                </>
              )}
            </Button>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleImport}
          className="hidden"
        />

        {importResult && (
          <div
            className={`rounded-lg border p-4 ${
              importResult.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
            }`}
          >
            <div className="flex items-start">
              {importResult.success ? (
                <CheckCircle className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
              ) : (
                <AlertCircle className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
              )}
              <div>
                <p className={`font-medium ${importResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {importResult.success ? 'Import Succesvol' : 'Import Mislukt'}
                </p>
                <p className={`mt-1 text-sm ${importResult.success ? 'text-green-600' : 'text-red-600'}`}>
                  {importResult.message}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h4 className="mb-2 font-medium text-gray-800">Bestandsformaat</h4>
          <div className="space-y-2 text-sm text-gray-600">
            <p>
              <strong>Export:</strong> genereert een JSON-bestand met al je recepten in een gestandaardiseerd formaat.
            </p>
            <p>
              <strong>Import:</strong> accepteert JSON-bestanden geëxporteerd vanuit KookInspiratie.
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Tip: exporteer regelmatig je recepten als backup voordat je grote wijzigingen maakt.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
